import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Home, Plus, X, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { apiErrorText } from '@/lib/vaultUpload';
import { AOP_META, VERIFICATION_LABEL, AopChip } from './aopMeta';
import ClaimFlatDialog from './ClaimFlatDialog';
import { fmtDop, fmtRupees } from './DirectoryTable';

const API = process.env.REACT_APP_BACKEND_URL;

function AopConfirm({ flat, onDone }) {
  const [want, setWant] = useState('');
  const [reason, setReason] = useState('');
  const send = async (value, why) => {
    try {
      const r = await axios.post(`${API}/api/allottees/flats/${flat.id}/mine/request`, { kind: 'aop', requested_value: value, reason: why });
      toast.success(r.data.result === 'confirmed' ? 'Thanks, AOP status confirmed' : 'Correction sent to the FBC team');
      setWant(''); onDone();
    } catch (e) { toast.error(apiErrorText(e, 'Could not send')); }
  };
  const pending = flat.requests?.some((r) => r.kind === 'aop' && r.status === 'pending');
  return (
    <div className="space-y-2" data-testid={`my-aop-${flat.id}`}>
      <p className="text-xs">Our record: <b>{AOP_META[flat.aop.status]?.long}</b> · {VERIFICATION_LABEL[flat.aop.verification_status]}</p>
      {pending ? <p className="text-xs text-amber-700">Your correction request is with the FBC team.</p> : (
        <div className="flex flex-wrap gap-2">
          {flat.aop.status !== 'unconfirmed' && flat.aop.verification_status !== 'confirmed_by_allottee' && (
            <Button size="sm" variant="outline" onClick={() => send(flat.aop.status)} data-testid={`my-aop-confirm-${flat.id}`}>That's correct</Button>
          )}
          <select value={want} onChange={(e) => setWant(e.target.value)} className="px-2 py-1 border rounded-md text-xs bg-background" data-testid={`my-aop-correct-${flat.id}`}>
            <option value="">{flat.aop.status === 'unconfirmed' ? 'Tell us your AOP status…' : 'Request a correction…'}</option>
            {Object.entries(AOP_META).filter(([k]) => k !== flat.aop.status && k !== 'unconfirmed').map(([k, m]) => <option key={k} value={k}>{m.long}</option>)}
          </select>
          {want && (
            <>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why? (e.g. member since 2024, receipt no.)" className="h-8 text-xs flex-1 min-w-[180px]" data-testid={`my-aop-reason-${flat.id}`} />
              <Button size="sm" onClick={() => send(want, reason)} data-testid={`my-aop-send-${flat.id}`}>Send</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Unconfirmed claim: register details stay hidden; FBC confirms only after seeing an allotment letter or agreement.
function PendingClaim({ flat, onChanged }) {
  const [busy, setBusy] = useState(false);
  const req = (flat.requests || []).find((r) => r.kind === 'claim' && r.status === 'pending');
  const docs = req?.proof_docs || [];
  const upload = async (file) => {
    if (!file || !req) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { await axios.post(`${API}/api/allottees/claim-requests/${req.id}/proof`, fd); toast.success('Proof uploaded. The FBC team will review it.'); onChanged(); }
    catch (e) { toast.error(apiErrorText(e, 'Upload failed')); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border p-3 text-sm space-y-2" data-testid={`my-flat-pending-${flat.id}`}>
      <div className="flex items-center gap-2"><Home className="h-4 w-4" /><b>{flat.flat_no}</b><Clock className="h-3.5 w-3.5 text-amber-600" /><span className="text-xs text-amber-700">Awaiting FBC confirmation</span></div>
      <p className="text-xs text-muted-foreground">Upload your allotment letter or agreement for sale. FBC confirms ownership from it before any register details for this flat are shown to you.</p>
      {docs.length > 0 && <p className="text-xs" data-testid={`claim-proof-list-${flat.id}`}>Uploaded: {docs.map((d) => d.filename).join(', ')}</p>}
      {req && (
        <label className="inline-flex">
          <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid={`claim-proof-input-${flat.id}`} />
          <span className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium cursor-pointer hover:bg-muted ${busy ? 'opacity-50' : ''}`}><Plus className="h-3.5 w-3.5 mr-1" />{docs.length ? 'Add another document' : 'Upload proof of ownership'}</span>
        </label>
      )}
    </div>
  );
}


function MyFlat({ flat, onChanged }) {
  const [cos, setCos] = useState(flat.co_allottees || []);
  const [phone, setPhone] = useState(flat.contact?.phone || '');
  const [area, setArea] = useState(flat.carpet_area_sqft ?? '');
  const [poss, setPoss] = useState(flat.possession_date || '');
  const canFillArea = [null, undefined, 'allottee'].includes(flat.carpet_area_source);
  const canFillPoss = [null, undefined, 'allottee'].includes(flat.possession_date_source);
  const [name, setName] = useState(flat.owner_name);
  if (flat.claim?.status !== 'confirmed') {
    return <p className="text-xs flex items-center gap-1 text-amber-700" data-testid={`my-flat-pending-${flat.id}`}><Clock className="h-3 w-3" />{flat.flat_no}: email verified, claim awaiting confirmation by the FBC team.</p>;
  }
  const save = async () => {
    try {
      await axios.patch(`${API}/api/allottees/flats/${flat.id}/mine`, {
        phone, co_allottees: cos.filter((c) => c.name.trim()),
        carpet_area_sqft: canFillArea && area !== '' ? Number(area) : undefined,
        possession_date: canFillPoss && poss ? poss : undefined,
      });
      if (name.trim() && name.trim() !== flat.owner_name) {
        await axios.post(`${API}/api/allottees/flats/${flat.id}/mine/request`, { kind: 'name', requested_value: name.trim(), reason: 'Corrected by allottee' });
      }
      toast.success('Saved'); onChanged();
    } catch (e) { toast.error(apiErrorText(e, 'Could not save')); }
  };
  return (
    <div className="rounded-md border p-3 space-y-3" data-testid={`my-flat-${flat.id}`}>
      <div className="flex items-center gap-2"><Home className="h-4 w-4" /><b className="text-sm">{flat.flat_no}</b><AopChip status={flat.aop.status} /></div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><p className="text-[11px] text-muted-foreground">Name on record (corrections are reviewed)</p><Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs" data-testid={`my-name-${flat.id}`} /></div>
        <div><p className="text-[11px] text-muted-foreground">Mobile (optional)</p><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-8 text-xs" data-testid={`my-phone-${flat.id}`} /></div>
      </div>
      <div>
        <p className="text-[11px] text-muted-foreground mb-1">Co-allottees</p>
        {cos.map((c, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <Input value={c.name} onChange={(e) => setCos(cos.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className="h-8 text-xs" placeholder="Name" data-testid={`my-co-name-${i}`} />
            <Input value={c.relation || ''} onChange={(e) => setCos(cos.map((x, j) => (j === i ? { ...x, relation: e.target.value } : x)))} className="h-8 text-xs w-32" placeholder="Relation" />
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setCos(cos.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCos([...cos, { name: '', relation: '' }])} data-testid={`my-co-add-${flat.id}`}><Plus className="h-3 w-3 mr-1" />Add co-allottee</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><p className="text-[11px] text-muted-foreground">RERA carpet area (sq ft){canFillArea ? '' : ' · from records'}</p>
          <Input type="number" step="0.01" value={area} onChange={(e) => setArea(e.target.value)} disabled={!canFillArea} className="h-8 text-xs" data-testid={`my-area-${flat.id}`} /></div>
        <div><p className="text-[11px] text-muted-foreground">Possession date{canFillPoss ? '' : ' · from records'}</p>
          <Input type="date" value={poss} onChange={(e) => setPoss(e.target.value)} disabled={!canFillPoss} className="h-8 text-xs" data-testid={`my-poss-${flat.id}`} /></div>
      </div>
      {flat.corpus_source && (
        <div className="grid sm:grid-cols-2 gap-2 text-xs" data-testid={`my-corpus-info-${flat.id}`}>
          <p><span className="text-muted-foreground">Membership application: </span><b data-testid={`my-applied-${flat.id}`}>{flat.membership_application_date ? `Submitted ${fmtDop(flat.membership_application_date)}` : 'Not submitted yet'}</b></p>
          <p><span className="text-muted-foreground">Corpus paid to society: </span><b data-testid={`my-corpus-${flat.id}`}>{fmtRupees(flat.corpus_paid)}</b></p>
          <p className="sm:col-span-2 text-[11px] text-muted-foreground">As reported in the developer's corpus statement. Only you and the project admins can see this.</p>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Your email and mobile are visible to verified members of this project according to the settings chosen by the project admins.</p>
      <Button size="sm" onClick={save} data-testid={`my-save-${flat.id}`}>Save my details</Button>
      <AopConfirm flat={flat} onDone={onChanged} />
    </div>
  );
}

export default function ClaimFlatCard({ projectId, onChanged }) {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => axios.get(`${API}/api/allottees/${projectId}/mine`).then((r) => setMine(r.data.flats)).catch(() => {});
  useEffect(() => { if (user && projectId) load(); }, [user, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user) return null;
  const changed = () => { load(); onChanged?.(); };
  return (
    <Card data-testid="claim-flat-card"><CardContent className="p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{mine.length ? 'My flats in the allottee register' : 'Is your flat in the allottee register?'}</p>
          <p className="text-xs text-muted-foreground">Claim it with an email code, then confirm your details and AOP status.</p>
        </div>
        <Button size="sm" variant={mine.length ? 'outline' : 'default'} onClick={() => setOpen(true)} data-testid="claim-flat-open">Claim {mine.length ? 'another' : 'my'} flat</Button>
      </div>
      {mine.map((f) => (f.claim?.status === 'confirmed' ? <MyFlat key={f.id} flat={f} onChanged={changed} /> : (
        <PendingClaim key={f.id} flat={f} onChanged={changed} />
      )))}
      <ClaimFlatDialog projectId={projectId} open={open} onOpenChange={setOpen} defaultEmail={user.email} onClaimed={changed} />
    </CardContent></Card>
  );
}
